import React, { ChangeEvent, Fragment, ReactElement, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import {
    Box,
    Backdrop,
    CircularProgress,
    Button,
    Typography,
    Grid2,
    TextField,
    IconButton,
    Tooltip,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/reducers';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { DetailsContainerBox, DetailsTableContainerBox } from '../../common/ResponsiveStyle';
import { filterPurchaseOrders } from './PurchaseOrdersHelper';
import { importClients } from '../../redux/actions/clientActions';
import { importSites } from '../../redux/actions/siteActions';
import { importPurchaseOrders, refreshPurchaseOrdersData } from '../../redux/actions/purchaseOrderActions';
import PurchaseOrderForm from './form/PurchaseOrderForm';
import PurchaseOrdersTable from './PurchaseOrdersTable';
import { clearPurchaseOrderCollapsible } from '../../redux/actions/collapsibleOpenStateActions';
import { isEmployeeProcurementManager } from '../../redux/selectors/employeeSelector';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';

export default function PurchaseOrdersList(): ReactElement {
    const purchaseOrders = useSelector((state: RootState) => state.purchaseOrders.purchaseOrders);
    const sites = useSelector((state: RootState) => state.sites.sites);
    const clients = useSelector((state: RootState) => state.clients.clients);

    const isProcurementManager = useAppSelector(isEmployeeProcurementManager);

    const [orderDocumentCodeFilter, setOrderDocumentCodeFilter] = React.useState<string>('');
    const [orderDestinationFilter, setOrderDestinationFilter] = React.useState<string>('');
    const [orderClientFilter, setOrderClientFilter] = React.useState<string>('');

    const [extendedDetails, setExtendedDetails] = useState<boolean>(false);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!purchaseOrders) {
            dispatch(importPurchaseOrders());
        }
    }, [dispatch]);

    useEffect(() => {
        if (!sites) {
            dispatch(importSites());
        }
    }, [dispatch]);

    useEffect(() => {
        if (!clients) {
            dispatch(importClients());
        }
    }, [dispatch]);

    //********  TIMER ********/

    useEffect(() => {
        const interval = setInterval(() => {
            dispatch(refreshPurchaseOrdersData());
        }, 60 * 1000);

        return () => clearInterval(interval);
    }, [dispatch]);

    //******  END TIMER ******/

    useEffect(() => {
        dispatch(clearPurchaseOrderCollapsible());
    }, [dispatch]);

    const handleDocumentCodeFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setOrderDocumentCodeFilter(value);
    };

    const handleDestinationFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setOrderDestinationFilter(value);
    };

    const handleClientFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setOrderClientFilter(value);
    };

    const clearAllFilters = () => {
        setOrderDocumentCodeFilter('');
        setOrderDestinationFilter('');
        setOrderClientFilter('');
    };

    const [openOrderForm, setOpenOrderForm] = useState<boolean>(false);

    const goToForm = () => {
        setOpenOrderForm(true);
    };

    const handleCloseOrderForm = () => {
        setOpenOrderForm(false);
    };

    const handleToggleExtendedDetails = () => {
        setExtendedDetails((prev) => !prev);
    };

    const filteredOrders = filterPurchaseOrders(
        purchaseOrders ?? [],
        orderDocumentCodeFilter,
        orderDestinationFilter,
        orderClientFilter,
        clients ?? [],
    );

    return purchaseOrders === null ? (
        <Fragment>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={!Boolean(purchaseOrders)}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </Fragment>
    ) : (
        <DetailsContainerBox
            sx={{
                p: 2,
                backgroundColor: (theme) => (theme.palette.mode === 'light' ? 'white' : theme.palette.grey[900]),
            }}
        >
            <Grid2 container sx={{ py: 2, px: { xs: 2, sm: 4 }, justifyContent: 'space-between', gap: 2 }}>
                <Grid2>
                    <Typography variant="h5">Narudžbenice</Typography>
                </Grid2>
                {isProcurementManager && (
                    <Grid2>
                        <Box display={'flex'} gap={2}>
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={goToForm}
                                startIcon={<AddIcon />}
                                sx={{ textTransform: 'none' }}
                            >
                                Nova narudžbenica
                            </Button>
                        </Box>
                    </Grid2>
                )}
            </Grid2>
            <hr />
            <Box sx={{ p: { xs: 1, sm: 4 }, pt: 2 }}>
                <Grid2 container sx={{ display: 'flex', gap: 2, flexDirection: 'start' }}>
                    <TextField
                        size="small"
                        sx={{ mb: 1, width: '200px' }}
                        placeholder="Br. dokumenta"
                        value={orderDocumentCodeFilter}
                        onChange={handleDocumentCodeFilterChange}
                    />
                    <TextField
                        size="small"
                        sx={{ mb: 1, width: '200px' }}
                        placeholder="Odredište"
                        value={orderDestinationFilter}
                        onChange={handleDestinationFilterChange}
                    />
                    <TextField
                        size="small"
                        sx={{ mb: 1, width: '200px' }}
                        placeholder="Dobavljač"
                        value={orderClientFilter}
                        onChange={handleClientFilterChange}
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    <Box>
                        <Button onClick={clearAllFilters}>Očisti filtere</Button>
                    </Box>
                </Grid2>
                <Box sx={{ p: 0, mt: 2 }} />
                <Box display="flex" justifyContent="end">
                    <IconButton onClick={handleToggleExtendedDetails} color="primary">
                        {extendedDetails ? (
                            <Tooltip title="Prikaži osnovne informacije">
                                <UnfoldLessIcon />
                            </Tooltip>
                        ) : (
                            <Tooltip title="Prikaži naloge za prijem">
                                <UnfoldMoreIcon />
                            </Tooltip>
                        )}
                    </IconButton>
                </Box>
                <Box sx={{ p: 0, mt: 2 }}></Box>

                <DetailsTableContainerBox>
                    <PurchaseOrdersTable purchaseOrders={filteredOrders ?? null} extendedDetails={extendedDetails} />
                </DetailsTableContainerBox>
            </Box>
            <PurchaseOrderForm closeForm={handleCloseOrderForm} open={openOrderForm} purchaseOrderId={undefined} />
        </DetailsContainerBox>
    );
}
